// src/pages/Trend/components/analysis/product-analysis/ProductListChart.js
import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { BOLSTER_COLORS, BOLSTER_CHART_STYLE } from '../common/BolsterColors';
import './ProductListChart.css';
import { Star, ImageOff } from 'lucide-react';

// Bolster AI Style Configuration
const BBC_STYLE = {
  FONT_FAMILY: BOLSTER_CHART_STYLE.FONT_FAMILY,
  TITLE_SIZE: '1.5rem',
  SUBTITLE_SIZE: '1rem',
  PRIMARY_COLOR: BOLSTER_COLORS.PRIMARY,
  SECONDARY_COLOR: BOLSTER_COLORS.WARNING,
  HIGHLIGHT_COLOR: BOLSTER_COLORS.DANGER,
  POSITIVE_COLOR: BOLSTER_COLORS.SUCCESS,
  NEGATIVE_COLOR: BOLSTER_COLORS.DANGER,
  AXIS_COLOR: BOLSTER_COLORS.TEXT_MUTED,
  TEXT_COLOR: BOLSTER_COLORS.TEXT_PRIMARY,
  BORDER_COLOR: BOLSTER_COLORS.BORDER
};

const StarGradientDef = () => (
  // display:none (Tailwind `hidden`) would stop the browser from rendering
  // the gradient defs, breaking every fill that references them — keep the
  // svg in the render tree at zero size instead
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="star-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#fcd34d' }} />
        <stop offset="100%" style={{ stopColor: '#f59e0b' }} />
      </linearGradient>
      <linearGradient id="star-gradient-half" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="50%" style={{ stopColor: '#f59e0b' }} />
        <stop offset="50%" style={{ stopColor: 'transparent' }} />
      </linearGradient>
    </defs>
  </svg>
);

// Status icon component
const StatusIcon = ({ status }) => {
  const normalizedStatus = status?.trim().toLowerCase() || '';

  if (normalizedStatus.includes('성장')) {
    return <span style={{ color: BOLSTER_COLORS.SUCCESS, fontSize: '10px', marginRight: '3px' }}>▲</span>;
  }
  if (normalizedStatus.includes('하락')) {
    return <span style={{ color: BOLSTER_COLORS.DANGER, fontSize: '10px', marginRight: '3px' }}>▼</span>;
  }
  if (normalizedStatus.includes('안정')) {
    return <span style={{ color: BOLSTER_COLORS.PRIMARY, fontSize: '10px', marginRight: '3px' }}>—</span>;
  }
  if (normalizedStatus.includes('초기')) {
    return <span style={{ color: BOLSTER_COLORS.WARNING, fontSize: '10px', marginRight: '3px' }}>◆</span>;
  }
  if (normalizedStatus.includes('변화')) {
    return <span style={{ color: BOLSTER_COLORS.PRIMARY, fontSize: '10px', marginRight: '3px' }}>↔</span>;
  }
  return null;
};

// Popularity indicator component
const PopularityIndicator = ({ popularity }) => {
  const cleanPopularity = popularity?.replace(/\s/g, '') || '';
  const levels = ['매우낮음', '낮음', '보통', '높음', '매우높음'];
  const level = levels.indexOf(cleanPopularity) + 1;

  return (
    <div style={{ display: 'flex', gap: '1px', alignItems: 'center', marginRight: '4px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <div
          key={i}
          style={{
            width: '2px',
            height: i <= level ? `${6 + i * 1.5}px` : '6px',
            backgroundColor: i <= level ?
              (level >= 4 ? '#22c55e' : level >= 3 ? '#3b82f6' : level >= 2 ? '#f59e0b' : '#ef4444')
              : '#e5e7eb',
            borderRadius: '1px',
            transition: 'all 0.3s ease'
          }}
        />
      ))}
    </div>
  );
};

const ProductListChart = ({ data }) => {
  const { translate } = useLanguage();
  const [activeListTab, setActiveListTab] = useState('filter1');
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [hoveredRow, setHoveredRow] = useState(null);
  const [sortColumns, setSortColumns] = useState([{ column: 'rating', direction: 'desc' }]);
  const [stageFilter, setStageFilter] = useState('all');
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  if (!data?.primary?.product_list) {
    return (
      <div className="analysis-error">
        {translate('trendPage.productAnalysis.errors.noProductListData')}
      </div>
    );
  }

  const handleProductClick = (groupId) => {
    // 새 탭에서 제품 상세 페이지 열기
    window.open(`/product/${groupId}`, '_blank');
  };

  const handleImageError = (productId) => {
    setImageLoadErrors(prev => ({
      ...prev,
      [productId]: true
    }));
  };

  // Translation helper functions
  const translateStatus = (status) => {
    if (!status || status === '모름' || status === '-') return '-';
    
    const statusMapping = {
      '성장': translate('trendPage.productAnalysis.productStatus.growing'),
      '안정': translate('trendPage.productAnalysis.productStatus.stable'),
      '초기': translate('trendPage.productAnalysis.productStatus.early'),
      '변화': translate('trendPage.productAnalysis.productStatus.changing'),
      '하락': translate('trendPage.productAnalysis.productStatus.declining'),
      '모름': '-'
    };
    
    return statusMapping[status.trim()] || status;
  };

  const translatePopularity = (popularity) => {
    if (!popularity) return '-';
    
    const popularityMapping = {
      '매우높음': translate('trendPage.productAnalysis.popularity.veryHigh'),
      '높음': translate('trendPage.productAnalysis.popularity.high'),
      '보통': translate('trendPage.productAnalysis.popularity.medium'),
      '낮음': translate('trendPage.productAnalysis.popularity.low'),
      '매우낮음': translate('trendPage.productAnalysis.popularity.veryLow')
    };
    
    const cleanPopularity = popularity.replace(/\s/g, '');
    return popularityMapping[cleanPopularity] || popularity;
  };

  const StarRating = ({ rating }) => {
    const score = rating === 'nan' ? 0 : parseFloat(rating);

    const renderStars = () => {
      const stars = [];
      const totalStars = 5;

      for (let i = 1; i <= totalStars; i++) {
        if (score >= i) {
          stars.push(
            <Star
              key={i}
              className="star star-filled"
              size={14}
              style={{
                fill: 'url(#star-gradient)',
                stroke: '#f59e0b',
                strokeWidth: '1px'
              }}
            />
          );
        } else if (score > i - 1 && score < i) {
          stars.push(
            <Star
              key={i}
              className="star star-half"
              size={14}
              style={{
                fill: 'url(#star-gradient-half)',
                stroke: '#f59e0b',
                strokeWidth: '1px'
              }}
            />
          );
        } else {
          stars.push(
            <Star
              key={i}
              className="star star-empty"
              size={14}
              style={{
                fill: 'none',
                stroke: '#d1d5db',
                strokeWidth: '1.5px'
              }}
            />
          );
        }
      }
      return stars;
    };

    return (
      <div className="star-rating-container" style={{ gap: '4px' }}>
        <div className="star-group" style={{ gap: '1px' }}>{renderStars()}</div>
        <span className="rating-text" style={{ fontSize: '0.75rem' }}>
          {score > 0 ? score.toFixed(1) : '-'}
        </span>
      </div>
    );
  };

  // Helper functions remain the same since they process API data values, not UI text
  const getStatusColorClass = (status) => {
    if (!status || status === '모름' || status === '-') return 'badge-gray';
    
    // Normalize string by trimming and converting to lowercase
    const normalizedStatus = status.trim().toLowerCase();
    
    // Map correct category values to colors
    if (normalizedStatus.includes('성장')) return 'badge-green';    // Growing - positive state
    if (normalizedStatus.includes('안정')) return 'badge-blue';     // Stable - neutral/positive state
    if (normalizedStatus.includes('초기')) return 'badge-yellow';   // Early stage - neutral state
    if (normalizedStatus.includes('변화')) return 'badge-purple';   // Changing - transitional state
    if (normalizedStatus.includes('하락')) return 'badge-red';      // Declining - negative state
    if (normalizedStatus.includes('모름')) return 'badge-gray';     // Unknown - neutral/gray state
    
    return 'badge-gray'; // Default fallback
  };

  const getPopularityColorClass = (popularity) => {
    if (!popularity) return 'badge-gray';
    
    // Remove any spaces for consistent matching
    const cleanPopularity = popularity.replace(/\s/g, '');
    
    switch (cleanPopularity) {
      case '매우높음':
        return 'badge-green';
      case '높음':
        return 'badge-yellow';
      case '보통':
        return 'badge-blue';
      case '낮음':
        return 'badge-orange';
      case '매우낮음':
        return 'badge-red';
      default:
        return 'badge-gray';
    }
  };

  const { primary, secondary } = data;
  const hasSecondary = !!secondary?.product_list;

  // Filter out products without images
  const filterProductsWithImages = (products) => {
    return products.filter(product => product.s3_url && product.s3_url.trim() !== '');
  };

  // Filter by stage
  const filterByStage = (products) => {
    if (stageFilter === 'all') return products;
    return products.filter(product => product.단계?.includes(stageFilter));
  };

  // Handle column header click for sorting (supports multi-column sorting with Shift+click)
  const handleColumnSort = (column, shiftKey) => {
    const existingIndex = sortColumns.findIndex(sc => sc.column === column);

    if (shiftKey && sortColumns.length < 3) {
      // Shift+click: Add or update secondary sort
      if (existingIndex >= 0) {
        // Toggle direction of existing sort
        const newSortColumns = [...sortColumns];
        newSortColumns[existingIndex].direction = newSortColumns[existingIndex].direction === 'asc' ? 'desc' : 'asc';
        setSortColumns(newSortColumns);
      } else {
        // Add as new secondary sort
        setSortColumns([...sortColumns, { column, direction: 'desc' }]);
      }
    } else {
      // Regular click: Set as primary sort
      if (existingIndex === 0 && sortColumns.length === 1) {
        // Toggle direction if it's the only sort column
        setSortColumns([{ column, direction: sortColumns[0].direction === 'asc' ? 'desc' : 'asc' }]);
      } else {
        // Set as new primary sort
        setSortColumns([{ column, direction: 'desc' }]);
      }
    }

    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Get value for sorting
  const getSortValue = (product, column) => {
    switch (column) {
      case 'rating':
        return parseFloat(product.평점) || 0;
      case 'reviews':
        return parseInt(product.리뷰수) || 0;
      case 'popularity':
        const popularityMap = { '매우낮음': 1, '낮음': 2, '보통': 3, '높음': 4, '매우높음': 5 };
        return popularityMap[product.인기도?.replace(/\s/g, '')] || 0;
      default:
        return 0;
    }
  };

  // Sort products based on multiple columns
  const sortProducts = (products) => {
    const sorted = [...products];

    sorted.sort((a, b) => {
      // Apply sorts in order (primary, secondary, tertiary)
      for (const { column, direction } of sortColumns) {
        const valueA = getSortValue(a, column);
        const valueB = getSortValue(b, column);

        if (valueA !== valueB) {
          return direction === 'asc' ? valueA - valueB : valueB - valueA;
        }
      }
      return 0;
    });

    return sorted;
  };

  // Get stage filter label
  const getStageFilterLabel = () => {
    if (stageFilter === 'all') return translate('trendPage.productAnalysis.stageFilter.all') || 'All Stages';
    return translateStatus(stageFilter);
  };

  // Get available stages from products
  const getAvailableStages = (products) => {
    const stages = new Set();
    products.forEach(product => {
      if (product.단계 && product.단계 !== '모름' && product.단계 !== '-') {
        stages.add(product.단계.trim());
      }
    });
    return ['all', ...Array.from(stages)];
  };

  // Calculate narratives for current data
  const getCurrentProducts = () => {
    return activeListTab === 'filter1' || !hasSecondary 
      ? primary.product_list 
      : secondary.product_list;
  };

  // Calculate weighted average rating
  const calculateWeightedAvgRating = (products) => {
    let totalRating = 0;
    let totalWeight = 0;
    
    products.forEach(p => {
      const rating = parseFloat(p.평점) || 0;
      const reviews = parseInt(p.리뷰수) || 0;
      if (rating > 0 && reviews > 0) {
        totalRating += rating * reviews;
        totalWeight += reviews;
      }
    });
    
    return totalWeight > 0 ? (totalRating / totalWeight).toFixed(2) : '0';
  };

  // Calculate simple narrative metrics
  const calculateSimpleMetrics = (products) => {
    const totalProducts = products.length;
    const weightedAvgRating = calculateWeightedAvgRating(products);
    const totalReviews = products.reduce((sum, p) => sum + (parseInt(p.리뷰수) || 0), 0);
    const avgReviews = totalProducts > 0 ? Math.round(totalReviews / totalProducts) : 0;
    
    // Count by status
    const growingCount = products.filter(p => p.단계?.includes('성장')).length;
    const stableCount = products.filter(p => p.단계?.includes('안정')).length;
    const decliningCount = products.filter(p => p.단계?.includes('하락')).length;
    
    // Count by popularity
    const highPopularityCount = products.filter(p => 
      p.인기도 === '매우높음' || p.인기도 === '높음'
    ).length;
    
    // Find top rated product
    const topRated = products.reduce((max, p) => {
      const rating = parseFloat(p.평점) || 0;
      const maxRating = parseFloat(max.평점) || 0;
      return rating > maxRating ? p : max;
    }, products[0]);

    return {
      totalProducts,
      weightedAvgRating,
      totalReviews,
      avgReviews,
      growingCount,
      stableCount,
      decliningCount,
      highPopularityCount,
      topRated
    };
  };

  // Create comparison narrative for two sets
  const createComparisonNarrative = () => {
    if (!hasSecondary) return null;
    
    const metrics1 = calculateSimpleMetrics(primary.product_list);
    const metrics2 = calculateSimpleMetrics(secondary.product_list);
    
    const ratingDiff = (parseFloat(metrics1.weightedAvgRating) - parseFloat(metrics2.weightedAvgRating)).toFixed(2);
    const productDiff = metrics1.totalProducts - metrics2.totalProducts;
    const growthDiff = metrics1.growingCount - metrics2.growingCount;
    
    return {
      set1: metrics1,
      set2: metrics2,
      ratingDiff,
      productDiff,
      growthDiff,
      higherRatingSet: parseFloat(ratingDiff) > 0 ? 'filter1' : parseFloat(ratingDiff) < 0 ? 'filter2' : 'equal'
    };
  };

  const renderTable = (products) => {
    const filteredProducts = filterProductsWithImages(products)
      .filter(product => !imageLoadErrors[product.group_id]); // Also filter out products with failed images

    const stageFilteredProducts = filterByStage(filteredProducts);
    const sortedProducts = sortProducts(stageFilteredProducts);

    // Pagination
    const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = sortedProducts.slice(startIndex, endIndex);

    const renderSortIcon = (column) => {
      const sortIndex = sortColumns.findIndex(sc => sc.column === column);
      const isActive = sortIndex >= 0;
      const sortInfo = isActive ? sortColumns[sortIndex] : null;

      if (!isActive) {
        // Show inactive sort indicators
        return (
          <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: '#d1d5db' }}>
            ↕
          </span>
        );
      }

      // Show active sort with priority number if multiple sorts
      return (
        <span style={{
          marginLeft: '6px',
          fontSize: '0.8rem',
          color: '#667eea',
          fontWeight: '600',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px'
        }}>
          {sortInfo.direction === 'asc' ? '↑' : '↓'}
          {sortColumns.length > 1 && (
            <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>
              {sortIndex + 1}
            </span>
          )}
        </span>
      );
    };

    return (
    <div className="trend-table-wrapper" style={{ overflow: 'visible', flexShrink: 0 }}>
      <table className="trend-product-table" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '60px' }} />
          <col style={{ width: 'auto' }} />
          <col style={{ width: '140px' }} />
          <col style={{ width: '80px' }} />
          <col style={{ width: '120px' }} />
          <col style={{ width: '100px' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="image-column" style={{ textAlign: 'center' }}></th>
            <th style={{ textAlign: 'center' }}>{translate('trendPage.productAnalysis.productTable.productName')}</th>
            <th
              style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
              onClick={(e) => handleColumnSort('rating', e.shiftKey)}
            >
              {translate('trendPage.productAnalysis.productTable.rating')}
              {renderSortIcon('rating')}
            </th>
            <th
              style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
              onClick={(e) => handleColumnSort('reviews', e.shiftKey)}
            >
              {translate('trendPage.productAnalysis.productTable.reviews')}
              {renderSortIcon('reviews')}
            </th>
            <th
              style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
              onClick={(e) => handleColumnSort('popularity', e.shiftKey)}
            >
              {translate('trendPage.productAnalysis.productTable.popularity')}
              {renderSortIcon('popularity')}
            </th>
            <th style={{ textAlign: 'center' }}>{translate('trendPage.productAnalysis.productTable.stage')}</th>
          </tr>
        </thead>
        <tbody>
          {currentProducts.map((product, index) => (
            <tr
              key={index}
              onMouseEnter={() => setHoveredRow(index)}
              onMouseLeave={() => setHoveredRow(null)}
              style={{
                transition: 'all 0.2s ease',
                background: hoveredRow === index ?
                  'linear-gradient(90deg, rgba(248,250,252,0.8) 0%, rgba(248,250,252,0.3) 100%)' :
                  'transparent'
              }}
            >
              <td className="image-cell">
                <div
                  className="image-wrapper"
                  style={{
                    transform: hoveredRow === index ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: hoveredRow === index ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {imageLoadErrors[product.group_id] ? (
                    <div className="image-fallback">
                      <ImageOff
                        size={24}
                        className="image-fallback-icon"
                      />
                    </div>
                  ) : (
                    <img
                      src={product.s3_url}
                      alt={product.제품명}
                      className="w-full h-full object-contain"
                      onError={() => handleImageError(product.group_id)}
                    />
                  )}
                </div>
              </td>
              <td
                className="product-name"
                onClick={() => handleProductClick(product.group_id)}
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{
                  color: hoveredRow === index ? '#667eea' : '#2563eb',
                  textDecoration: hoveredRow === index ? 'underline' : 'none',
                  fontWeight: '500',
                  marginBottom: '2px',
                  fontSize: '0.8rem'
                }}>
                  {product.제품명}
                </div>
                <div style={{
                  fontSize: '0.7rem',
                  color: '#9ca3af',
                  fontWeight: '400'
                }}>
                  {product.브랜드명}
                </div>
              </td>
              <td>
                <StarRating rating={product.평점} />
              </td>
              <td>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontWeight: '600',
                  color: '#475569',
                  fontSize: '0.75rem',
                  padding: '0.15rem 0.5rem',
                  background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.5) 100%)',
                  borderRadius: '4px',
                  minWidth: '50px',
                  justifyContent: 'center'
                }}>
                  {product.리뷰수 !== '0.0' ? parseInt(product.리뷰수).toLocaleString() : '-'}
                </span>
              </td>
              <td>
                <span
                  className={`popularity-badge ${getPopularityColorClass(product.인기도)}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transform: hoveredRow === index ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <PopularityIndicator popularity={product.인기도} />
                  <span>{translatePopularity(product.인기도)}</span>
                </span>
              </td>
              <td>
                <span
                  className={`status-badge ${getStatusColorClass(product.단계)}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    transform: hoveredRow === index ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <StatusIcon status={product.단계} />
                  {translateStatus(product.단계)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          marginTop: '12px',
          paddingTop: '8px',
          borderTop: '1px solid #e5e7eb',
          flexShrink: 0
        }}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            onMouseEnter={(e) => {
              if (currentPage !== 1) {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.borderColor = '#d1d5db';
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 1) {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }
            }}
            style={{
              padding: '6px 12px',
              fontSize: '0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
              color: currentPage === 1 ? '#9ca3af' : '#374151',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            ← Prev
          </button>

          <span style={{
            fontSize: '0.75rem',
            color: '#666666',
            fontWeight: '500',
            minWidth: '80px',
            textAlign: 'center'
          }}>
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            onMouseEnter={(e) => {
              if (currentPage !== totalPages) {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.borderColor = '#d1d5db';
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== totalPages) {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }
            }}
            style={{
              padding: '6px 12px',
              fontSize: '0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              backgroundColor: currentPage === totalPages ? '#f9fafb' : 'white',
              color: currentPage === totalPages ? '#9ca3af' : '#374151',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
    );
  };

  const currentProducts = getCurrentProducts();
  const metrics = calculateSimpleMetrics(currentProducts);
  const comparisonNarrative = createComparisonNarrative();

  // Create simplified subtitle with key metrics
  const createSubtitle = () => {
    if (!hasSecondary) {
      // Single set - show products and rating
      const avgRating = parseFloat(metrics.weightedAvgRating);

      return (
        <>
          {metrics.totalProducts} {translate('trendPage.productAnalysis.narratives.products')}, {translate('trendPage.productAnalysis.narratives.rating')} {avgRating.toFixed(2)}
        </>
      );
    } else {
      // Comparison - show both filter stats
      const { set1, set2 } = comparisonNarrative;

      return (
        <>
          <span style={{
            fontWeight: '600',
            color: BBC_STYLE.PRIMARY_COLOR
          }}>
            {translate('trendPage.filters.set1')}
          </span>
          : {set1.totalProducts} {translate('trendPage.productAnalysis.narratives.products')}, {translate('trendPage.productAnalysis.narratives.rating')} {set1.weightedAvgRating}
          <span style={{
            margin: '0 10px',
            color: '#D0D0D0',
            fontWeight: '300'
          }}>｜</span>
          <span style={{
            fontWeight: '600',
            color: BBC_STYLE.SECONDARY_COLOR
          }}>
            {translate('trendPage.filters.set2')}
          </span>
          : {set2.totalProducts} {translate('trendPage.productAnalysis.narratives.products')}, {translate('trendPage.productAnalysis.narratives.rating')} {set2.weightedAvgRating}
        </>
      );
    }
  };

  return (
    <div style={{
      fontFamily: BBC_STYLE.FONT_FAMILY,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'visible'
    }}>
      <StarGradientDef />
      {/* Header Section - Fixed at top */}
      <div style={{
        marginBottom: '1rem',
        flexShrink: 0
      }}>
        {/* Title */}
        <h3 style={{
          fontSize: '0.85rem',
          fontWeight: '600',
          color: BBC_STYLE.TEXT_COLOR,
          margin: '0 0 3px 0'
        }}>
          {translate('trendPage.productAnalysis.productListSection.title')}
        </h3>

        {/* Subtitle - Only comparison metrics */}
        <p style={{
          fontSize: '0.85rem',
          color: '#666666',
          margin: '0 0 8px 0',
          fontWeight: '400',
          lineHeight: '1.3'
        }}>
          {createSubtitle()}
        </p>
      </div>

      {/* Table Section - No scroll, using pagination */}
      <div className="product-list-chart" style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible'
      }}>
        {hasSecondary && (
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '12px',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '8px'
        }}>
          <button
            onClick={() => {
              setActiveListTab('filter1');
              setCurrentPage(1);
            }}
            onMouseEnter={(e) => {
              if (activeListTab !== 'filter1') {
                e.currentTarget.style.color = '#6b7280';
              }
            }}
            onMouseLeave={(e) => {
              if (activeListTab !== 'filter1') {
                e.currentTarget.style.color = '#9ca3af';
              }
            }}
            style={{
              padding: '4px 12px',
              fontSize: '0.75rem',
              fontWeight: activeListTab === 'filter1' ? '600' : '400',
              color: activeListTab === 'filter1' ? BBC_STYLE.PRIMARY_COLOR : '#9ca3af',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeListTab === 'filter1' ? `2px solid ${BBC_STYLE.PRIMARY_COLOR}` : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {translate('trendPage.filters.set1')}
          </button>
          <button
            onClick={() => {
              setActiveListTab('filter2');
              setCurrentPage(1);
            }}
            onMouseEnter={(e) => {
              if (activeListTab !== 'filter2') {
                e.currentTarget.style.color = '#6b7280';
              }
            }}
            onMouseLeave={(e) => {
              if (activeListTab !== 'filter2') {
                e.currentTarget.style.color = '#9ca3af';
              }
            }}
            style={{
              padding: '4px 12px',
              fontSize: '0.75rem',
              fontWeight: activeListTab === 'filter2' ? '600' : '400',
              color: activeListTab === 'filter2' ? BBC_STYLE.SECONDARY_COLOR : '#9ca3af',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeListTab === 'filter2' ? `2px solid ${BBC_STYLE.SECONDARY_COLOR}` : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {translate('trendPage.filters.set2')}
          </button>
        </div>
      )}

        {/* Stage Filter */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '8px',
          position: 'relative'
        }}>
          <button
            onClick={() => setShowStageDropdown(!showStageDropdown)}
            style={{
              padding: '4px 10px',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.7rem',
              fontWeight: '500',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M8 12h8M11 18h2" />
            </svg>
            {getStageFilterLabel()}
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{
                transform: showStageDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {/* Stage Dropdown Menu */}
          {showStageDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              zIndex: 10,
              minWidth: '140px',
              overflow: 'hidden'
            }}>
              {getAvailableStages(filterProductsWithImages(getCurrentProducts())).map((stage) => (
                <div
                  key={stage}
                  onClick={() => {
                    setStageFilter(stage);
                    setShowStageDropdown(false);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    color: stageFilter === stage ? '#667eea' : '#374151',
                    backgroundColor: stageFilter === stage ? '#f5f7ff' : 'white',
                    fontWeight: stageFilter === stage ? '600' : '400',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    if (stageFilter !== stage) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = stageFilter === stage ? '#f5f7ff' : 'white';
                  }}
                >
                  {stage !== 'all' && <StatusIcon status={stage} />}
                  {stage === 'all'
                    ? (translate('trendPage.productAnalysis.stageFilter.all') || 'All Stages')
                    : translateStatus(stage)
                  }
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="list-tab-content">
          {activeListTab === 'filter1' || !hasSecondary 
            ? renderTable(primary.product_list)
            : renderTable(secondary.product_list)
          }
        </div>
      </div>
    </div>
  );
};

ProductListChart.propTypes = {
  data: PropTypes.shape({
    primary: PropTypes.shape({
      product_list: PropTypes.arrayOf(
        PropTypes.shape({
          s3_url: PropTypes.string.isRequired,
          제품명: PropTypes.string.isRequired,
          브랜드명: PropTypes.string.isRequired,
          평점: PropTypes.string,
          리뷰수: PropTypes.string,
          단계: PropTypes.string.isRequired,
          인기도: PropTypes.string.isRequired
        })
      ).isRequired
    }).isRequired,
    secondary: PropTypes.shape({
      product_list: PropTypes.arrayOf(
        PropTypes.shape({
          s3_url: PropTypes.string.isRequired,
          제품명: PropTypes.string.isRequired,
          브랜드명: PropTypes.string.isRequired,
          평점: PropTypes.string,
          리뷰수: PropTypes.string,
          단계: PropTypes.string.isRequired,
          인기도: PropTypes.string.isRequired
        })
      )
    })
  }).isRequired
};

export default memo(ProductListChart);
